import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import type { DefaultError, QueryKey } from '@tanstack/query-core';
import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';

import { BaseStepConfig, Endpoints, TransitStepConfig } from '@/api';
import { useFetch } from '@/utils/fetch';

import { canActOnBehalfOfNonDigitalizedWorkshop, hasPermission, UserFromGetSession } from './user';

export const getOrigin = (shipment: ShipmentWithRelations) => {
  return shipment.originClient ?? shipment.originEntity;
};

export const getDestination = (shipment: ShipmentWithRelations) => {
  return shipment.destinationClient ?? shipment.destinationEntity;
};

export const getDepartureDate = (shipment: ShipmentWithRelations) => {
  const firstTransit = shipment.trackingEvents?.find((h) => h.status === 'in_transit');

  return firstTransit ? new Date(firstTransit.date) : null;
};

export const getArrivalDate = (shipment: ShipmentWithRelations) => {
  const firstDelivered = shipment.trackingEvents?.find((h) => h.status === 'delivered');

  return firstDelivered ? new Date(firstDelivered.date) : null;
};

export const canReceptionBeVerifiedBy = (
  shipment: ShipmentWithRelations | undefined,
  user: UserFromGetSession | undefined | null
) => {
  if (!user || !shipment) {
    return false;
  }

  if (shipment.destinationEntity?.workshopConfig?.nonDigitalized) {
    return hasPermission(user, 'create_shipment_from_origin', [
      {
        entityId: shipment.originEntityId,
      },
    ]);
  }

  if (shipment.destinationEntityId) {
    return hasPermission(user, 'verify_shipment_reception', [
      {
        entityId: shipment.destinationEntityId,
      },
    ]);
  }

  return false;
};

export const canBeCreatedBy = (
  shipment: ShipmentWithRelations | undefined,
  user: UserFromGetSession | undefined | null
) => {
  if (!user || !shipment || !shipment.originEntity) {
    return false;
  }

  if (shipment.originEntity.workshopConfig?.nonDigitalized) {
    const request = (shipment.articles[0].article.request ??
      shipment.articles[0].article.archivedRequest)!;

    return canActOnBehalfOfNonDigitalizedWorkshop({
      user,
      request,
      workflow: request.workflow,
    });
  }

  return hasPermission(user, 'create_shipment_from_origin', [
    { entityId: shipment.originEntity.id },
  ]);
};

export const useGetCarrierName = () => {
  const { t } = useLingui();

  return (carrier: ShipmentWithRelations['carrier']) => {
    if (carrier === 'private-carrier') {
      return t({ id: 'shipment.carrier.private-carrier', message: 'Private carrier' });
    }

    return SHIPMENT_CARRIERS.find((c) => c.id === carrier)?.name ?? carrier ?? '-';
  };
};

export const useCarrierName = (shipment?: ShipmentWithRelations) => {
  const getCarrierName = useGetCarrierName();

  return getCarrierName(shipment?.carrier ?? null);
};

export type ShipmentWithRelations = Endpoints['GET /shipments/:id']['response'];
export type ArticleWithRelations = ShipmentWithRelations['articles'][number]['article'];

type ShipmentsResponse = Endpoints['GET /shipments']['response'];

export const useShipments = (
  params?: Endpoints['GET /shipments']['query'],
  options: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    refetchInterval?: UseQueryOptions<ShipmentsResponse>['refetchInterval'];
  } = {}
) => {
  const fetch = useFetch<Endpoints['GET /shipments']>();

  return useQuery({
    queryKey: ['shipments', params],
    queryFn: () => fetch('/shipments', params),
    enabled: options.enabled,
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    refetchInterval: options.refetchInterval,
  });
};

export const useShipment = (id?: string) => {
  const fetch = useFetch<Endpoints['GET /shipments/:id']>();

  return useQuery({
    queryKey: ['shipments', id],
    queryFn: () => fetch(`/shipments/${id!}`),
    enabled: !!id,
    refetchInterval: () =>
      document.visibilityState === 'hidden' ? 10 * 60 * 1_000 : 50 * 60 * 1_000, // Refetch every ten minutes if the focus is outside, every 5 minutes otherwise
    refetchIntervalInBackground: true,
  });
};

export const useComments = ({
  shipmentId,
  ...params
}: {
  shipmentId: string;
  limit?: number;
  offset?: number;
}) => {
  const fetch = useFetch<Endpoints['GET /shipments/:id/comments']>();

  return useQuery({
    queryKey: ['comments', shipmentId, params],
    queryFn: async () => {
      // FIXME: Is this limit ok? Should we handle pagination?
      const defaultParams = { limit: 250, offset: 0 };

      return await fetch(`/shipments/${shipmentId}/comments`, {
        ...defaultParams,
        ...params,
      });
    },
  });
};

export const useCreateComment = ({ shipmentId }: { shipmentId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /shipments/:id/comments']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /shipments/:id/comments']['body']) => {
      return fetch(`/shipments/${shipmentId}/comments`, undefined, {
        method: 'POST',
        body,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', shipmentId] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useUpdateComment = ({ shipmentId }: { shipmentId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /shipments/:id/comments/:commentId']>();

  return useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) => {
      return fetch(`/shipments/${shipmentId}/comments/${commentId}`, undefined, {
        method: 'PATCH',
        body: { content },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', shipmentId] });
    },
  });
};

export const useDeleteComment = ({ shipmentId }: { shipmentId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /shipments/:id/comments/:commentId']>();

  type CommentsQueryData = Endpoints['GET /shipments/:id/comments']['response'];

  return useMutation({
    mutationFn: ({ commentId }: { commentId: string }) => {
      const commentsQueries = queryClient.getQueriesData<CommentsQueryData>({
        queryKey: ['comments', shipmentId],
      });

      commentsQueries.forEach(([queryKey, queryData]) => {
        if (!queryData) {
          return;
        }

        const comments = queryData.comments.filter((comment) => comment.id !== commentId);

        queryClient.setQueryData(queryKey, {
          comments,
          meta: {
            ...queryData.meta,
            count: Math.max(0, queryData.meta.count - 1),
          },
        });
      });

      return fetch(`/shipments/${shipmentId}/comments/${commentId}`, undefined, {
        method: 'DELETE',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', shipmentId] });
    },
  });
};

export const useRemoveArticleFromShipment = ({ shipmentId }: { shipmentId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /shipments/:id/articles/:articleId']>();

  return useMutation({
    mutationKey: ['shipments', shipmentId],
    mutationFn: ({ articleId }: { articleId: string }) => {
      return fetch(`/shipments/${shipmentId}/articles/${articleId}`, undefined, {
        method: 'DELETE',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments', shipmentId] });
      queryClient.invalidateQueries({ queryKey: ['shipments/articles'] });
    },
  });
};

export const SHIPMENT_STEPS = [
  {
    name: msg({ id: 'shipment.status.preparation', message: 'Preparation' }),
    id: 'preparation',
    statuses: ['draft'],
    color: 'black',
  },
  {
    name: msg({ id: 'shipment.status.finalisation', message: 'Finalisation' }),
    id: 'finalisation',
    statuses: ['created'],
    color: 'black',
  },
  {
    name: msg({ id: 'shipment.status.handover', message: 'Handover' }),
    id: 'handover',
    statuses: ['pending-dropoff', 'pending-pickup'],
    color: 'blue',
  },
  {
    name: msg({ id: 'shipment.status.in-transit', message: 'In transit' }),
    id: 'in-transit',
    statuses: ['in-transit'],
    color: 'purple',
  },
  {
    hiddenInDestinationClient: true,
    name: msg({ id: 'shipment.status.verification', message: 'Verification' }),
    id: 'verification',
    statuses: ['received'],
    color: 'orange',
  },
  {
    hidden: true,
    name: msg({ id: 'shipment.status.validated', message: 'Validated' }),
    id: 'validated',
    statuses: ['validated'],
    color: 'green',
  },
] as const;

export type ShipmentStep = (typeof SHIPMENT_STEPS)[number]['id'];

/* eslint-disable lingui/no-unlocalized-strings */
export const SHIPMENT_CARRIERS = [
  {
    id: 'chronopost',
    name: 'Chronopost',
    dropoffPoints: 'https://www.chronopost.fr/expeditionAvancee/ounoustrouver.html',
  },
  {
    id: 'dhl',
    name: 'DHL',
    dropoffPoints: 'https://locator.dhl.com/',
  },
  {
    id: 'mondial_relay',
    name: 'Mondial Relay',
    dropoffPoints:
      'https://www.mondialrelay.fr/trouver-le-point-relais-le-plus-proche-de-chez-moi/',
  },
  {
    id: 'royal_mail',
    name: 'Royal Mail',
    dropoffPoints: 'https://www.royalmail.com/services-near-you#/',
  },
  {
    id: 'ups',
    name: 'UPS',
    dropoffPoints: 'https://www.ups.com/dropoff',
  },
  {
    id: 'tnt_france',
    name: 'TNT France',
    dropoffPoints: 'https://www.tnt.fr/fr-fr/dropoff',
  },
  {
    id: 'fedex',
    name: 'Fedex',
    dropoffPoints: 'https://www.fedex.com/fr-fr/dropoff',
  },
] as const;

export const SHIPMENT_CARRIERS_WITH_MORE_OPTIONS = [
  ...SHIPMENT_CARRIERS.map(({ id }) => id),
  'private-carrier',
] as const;

export const SHIPMENT_CARRIER_SERVICES = [
  {
    id: 'chrono_reverse_13',
    name: 'Chrono Reverse 13',
  },
  {
    id: 'chrono_13_instance_agence',
    name: 'Chrono 13 Instance Agence',
  },
  {
    id: 'economy_select',
    name: 'Economy Select',
  },
  {
    id: 'domestic_express',
    name: 'Domestic Express',
  },
  {
    id: 'express_worldwide',
    name: 'Express Worldwide',
  },
  {
    id: 'express_worldwide_ue',
    name: 'Express Worldwide UE',
  },
  {
    id: 'economy_select_ue',
    name: 'Economy Select UE',
  },
  {
    id: 'point_relais',
    name: 'Point Relais',
  },
  {
    id: 'standard',
    name: 'Standard',
  },
  {
    id: 'tracked_returns_24',
    name: 'Tracked Returns 24',
  },
  {
    id: 'tracked_48',
    name: 'Tracked 48',
  },
  {
    id: 'express_domestic',
    name: 'Express Domestic',
  },
  {
    id: 'fedex_international_economy',
    name: 'Fedex International Economy',
  },
] as const;
/* eslint-enable lingui/no-unlocalized-strings */

export type ShipmentCarrierWithMoreOptions = (typeof SHIPMENT_CARRIERS_WITH_MORE_OPTIONS)[number];
export type ShipmentCarrierService =
  (typeof SHIPMENT_CARRIER_SERVICES)[number]['id'] | 'private-carrier';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SHIPMENT_HANDOVER = ['dropoff', 'pickup', 'automatic_pickup'] as const;

export type ShipmentHandover = (typeof SHIPMENT_HANDOVER)[number];

export const useCreateShipment = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /shipments']>();

  return useMutation({
    mutationFn: (data: {
      origin: string;
      destination: string;
      articles: string[];
      carrierService?: ShipmentCarrierService;
      handover?: ShipmentHandover;
      pickupDate?: string;
      trackingId?: string;
    }) => fetch('/shipments', undefined, { method: 'POST', body: data }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useUpdateDraftShipment = (id: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /shipments/:id']>();

  return useMutation({
    mutationFn: (data: {
      origin?: string;
      destination?: string;
      articles?: string[];
      carrierService?: ShipmentCarrierService;
      handover?: ShipmentHandover;
      pickupDate?: string;
      trackingId?: string;
    }) => fetch(`/shipments/${id}`, undefined, { method: 'PATCH', body: data }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useUpdateShipmentTrackingId = (id: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /shipments/:id/tracking-id']>();

  return useMutation({
    mutationFn: (trackingId: string | null) =>
      fetch(`/shipments/${id}/tracking-id`, undefined, { method: 'PATCH', body: { trackingId } }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useCompletePreparation = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /shipments/:id/complete-preparation']>();

  return useMutation({
    mutationFn: (id: string) =>
      fetch(`/shipments/${id}/complete-preparation`, undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useCompleteFinalisation = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /shipments/:id/complete-finalisation']>();

  return useMutation({
    mutationFn: (id: string) =>
      fetch(`/shipments/${id}/complete-finalisation`, undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useCompleteHandover = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /shipments/:id/complete-handover']>();

  return useMutation({
    mutationFn: (id: string) =>
      fetch(`/shipments/${id}/complete-handover`, undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useCompleteInTransit = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /shipments/:id/complete-in-transit']>();

  return useMutation({
    mutationFn: (id: string) =>
      fetch(`/shipments/${id}/complete-in-transit`, undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useVerifyArticle = ({
  shipmentId,
  articleId,
}: {
  shipmentId: string;
  articleId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /shipments/:id/verify-article/:articleId']>();

  return useMutation({
    mutationFn: (body: { verified: boolean; issue?: string | null }) =>
      fetch(`/shipments/${shipmentId}/verify-article/${articleId}`, undefined, {
        method: 'POST',
        body,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments', shipmentId] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useResolveShipmentIssue = ({
  shipmentId,
  articleId,
}: {
  shipmentId: string;
  articleId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /shipments/:id/resolve-issue/:articleId']>();

  return useMutation({
    mutationFn: (body: { issueResolution: string }) =>
      fetch(`/shipments/${shipmentId}/resolve-issue/${articleId}`, undefined, {
        method: 'POST',
        body,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['shipments', shipmentId] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useArchiveShipmentIssue = ({
  shipmentId,
  articleId,
}: {
  shipmentId: string;
  articleId: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /shipments/:id/archive-issue/:articleId']>();

  return useMutation({
    mutationFn: (body: { archivalReason: string }) =>
      fetch(`/shipments/${shipmentId}/archive-issue/${articleId}`, undefined, {
        method: 'POST',
        body,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['shipments', shipmentId] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useCompleteVerification = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /shipments/:id/complete-verification']>();

  return useMutation({
    mutationFn: (id: string) =>
      fetch(`/shipments/${id}/complete-verification`, undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useAvailableCarriers = (
  {
    organizationId,
    originId,
    destinationId,
  }: {
    organizationId?: string;
    originId?: string;
    destinationId?: string;
  },
  options?: { enabled: boolean }
) => {
  const fetch = useFetch<Endpoints['GET /shipments/available-carriers']>();

  return useQuery({
    queryKey: ['shipments', 'available-carriers', { organizationId, originId, destinationId }],
    queryFn: () =>
      fetch('/shipments/available-carriers', {
        organizationId: organizationId!,
        originId: originId!,
        destinationId: destinationId!,
      }),
    enabled: options?.enabled !== false && !!organizationId && !!originId && !!destinationId,
  });
};

export const useInfiniteActivities = ({ shipmentId }: { shipmentId: string }) => {
  const fetch = useFetch<Endpoints['GET /shipments/:id/activities']>();

  return useInfiniteQuery<
    Endpoints['GET /shipments/:id/activities']['response'],
    DefaultError,
    InfiniteData<Endpoints['GET /shipments/:id/activities']['response']>,
    QueryKey,
    string | null
  >({
    queryFn: ({ pageParam }) => {
      const requestParams = pageParam ? { before: pageParam } : undefined;

      return fetch(`/shipments/${shipmentId}/activities`, requestParams);
    },
    queryKey: ['activities', shipmentId],
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.meta.next,
  });
};

export const useShipShipment = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /shipments/:id/ship']>();

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      fetch(`/shipments/${id}/ship`, undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useServicePoint = (id?: number | string, initialData?: ServicePoint) => {
  const fetch = useFetch<Endpoints['GET /shipments/service-points/:id']>();

  return useQuery({
    queryKey: ['shipments', 'service-points', { id }],
    queryFn: () => fetch(`/shipments/service-points/${id}`),
    enabled: !!id,
    initialData,
  });
};

export type ServicePoint = Endpoints['GET /shipments/service-points/:id']['response'];

export const useArticleOngoingShipment = (article: { id: string; step: BaseStepConfig | null }) => {
  const isInTransit = article.step?.step === 'transit';

  const stepConfig = article.step?.config as TransitStepConfig['config'] | undefined;
  const isClientPickup =
    stepConfig?.originType === 'store' &&
    stepConfig?.destinationType === 'client' &&
    stepConfig?.clientPickup;

  const { data: { shipments } = { shipments: [] }, isLoading } = useShipments(
    {
      articleId: article.id,
      steps: ['preparation', 'finalisation', 'handover', 'in-transit', 'verification'],
    },
    {
      enabled: isInTransit && !isClientPickup,
    }
  );

  if (!isInTransit || isClientPickup) {
    return { shipment: undefined, isLoading: false };
  }

  return { shipment: shipments[0], isLoading };
};

export const useArticleShipmentWithIssue = (article: { id: string }) => {
  const { data: { shipments } = { shipments: [] }, isLoading } = useShipments({
    articleId: article.id,
  });

  const shipment = shipments.find(({ articles }) => {
    return !!articles.find(
      ({ articleId, issue, issueResolution }) =>
        articleId === article.id && issue && !issueResolution
    );
  });

  return { shipment, isLoading };
};
